import React, { useEffect, useMemo, useState, useRef } from 'react';
import {
  Table,
  Tag,
  Space,
  Button,
  Typography,
  message,
  Breadcrumb,
  Tooltip,
  Input,
  Empty,
  Select,
  Row,
  Col,
  Popover,
  AutoComplete,
} from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import {
  DeleteOutlined,
  PlusOutlined,
  UserOutlined,
  SettingOutlined,
  ExportOutlined,
  PlusCircleOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import { v2ArticlesApi, api, publicSamplesApi } from '../../../api';
import { getUserProfile } from '../../../auth';
import RegisterPromptModal from '../../Demo/RegisterPromptModal';

const statusMap = {
  queued: { text: '準備中', color: 'default' },
  step1_generating: { text: '構成案生成中', color: 'processing' },
  step2: { text: 'ベース選択中', color: 'processing' },
  step3: { text: '構成編集中', color: 'geekblue' },
  step4_1: { text: '執筆計画中', color: 'cyan' },
  step4_2: { text: '本文生成中', color: 'blue' },
  step4_3: { text: '推敲中', color: 'purple' },
  step4_4: { text: '最終調整中', color: 'orange' },
  step4: { text: '執筆計画中', color: 'cyan' }, // フォールバック（STEP4.1相当）
  step4_ready: { text: '執筆計画中', color: 'cyan' }, // STEP4.1相当
  writing: { text: '本文生成中', color: 'blue' },
  reviewing: { text: '推敲中', color: 'purple' },
  finalizing: { text: '最終調整中', color: 'orange' },
  draft: { text: 'ベース選択中', color: 'processing' },
  generating: { text: '本文生成中', color: 'blue' },
  completed: { text: '生成完了', color: 'success' },
  error: { text: 'エラー', color: 'error' },
  cancelled: { text: 'キャンセル', color: 'default' },
};

const normalizeStatus = rec => {
  const s = String(rec.status || '').toLowerCase();
  const step = String(rec.current_step || '').toLowerCase();

  // ⭐ status='completed' を最優先で判定（生成完了）
  if (s === 'completed') return 'completed';

  // step4_completed は current_step に基づいて判定
  if (s === 'step4_completed') {
    if (step === '4.4' || step === 'step4.4') return 'step4_4'; // 最終調整中
    if (step === '4.3' || step === 'step4.3') return 'step4_3'; // 推敲中
    // 本文生成完了後は推敲フェーズへ移行
    return 'step4_3';
  }

  // current_step を詳細に判定
  if (step === '4.1' || step === 'step4.1') return 'step4_1'; // 執筆計画中
  if (step === '4.2' || step === 'step4.2') return 'step4_2'; // 本文生成中
  if (step === '4.3' || step === 'step4.3') return 'step4_3'; // 推敲中
  if (step === '4.4' || step === 'step4.4') return 'step4_4'; // 最終調整中
  if (step.startsWith('4')) return 'step4'; // その他のSTEP4（フォールバック）
  if (step === 'step3' || step === '3') return 'step3'; // 構成編集中
  if (step === 'step2' || step === '2') return 'step2'; // ベース選択中

  // STEP1完了後、構成案生成中（status=draftでcurrent_step=step1のまま）
  if ((step === 'step1' || step === '1') && s === 'draft') return 'step1_generating';

  // status で判断（新しいステータスに対応）
  if (s === 'step4_ready') return 'step4_ready'; // 執筆計画中
  if (s === 'writing') return 'writing'; // 本文生成中
  if (s === 'reviewing') return 'reviewing'; // 推敲中
  if (s === 'finalizing') return 'finalizing'; // 最終調整中
  if (s === 'error') return 'error';
  if (s === 'cancelled') return 'cancelled';

  // それ以外（generating/queuedなど）は準備中扱い
  return 'queued';
};

const ResultList = ({ isAdmin = false }) => {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pageState, setPageState] = useState({ current: 1, pageSize: 20 });
  const [searchText, setSearchText] = useState('');
  const [userRole, setUserRole] = useState('user');
  const [isGuest, setIsGuest] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [registerAction, setRegisterAction] = useState('');
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [tagFilter, setTagFilter] = useState(null);
  const [allTags, setAllTags] = useState([]);
  const navigate = useNavigate();
  
  // 削除確認用（2回クリックで削除）
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const deleteConfirmTimerRef = useRef(null);
  
  // タグ追加用
  const [tagInputVisible, setTagInputVisible] = useState({});
  const [tagInputValue, setTagInputValue] = useState('');

  // ユーザー情報の取得
  useEffect(() => {
    const fetchUserInfo = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        setIsGuest(true);
        return;
      }
      try {
        const profile = await getUserProfile();
        setUserRole(profile.role);

        if (profile.role === 'admin' || profile.role === 'master') {
          const response = await api.get('/users/', {
            params: { page_size: 1000 },
          });
          setUsers(response.data?.users || []);
        }
      } catch (e) {
        console.error('Failed to get user profile:', e);
        setIsGuest(true);
      }
    };
    fetchUserInfo();
  }, []);

  const load = async (page = pageState.current, pageSize = pageState.pageSize) => {
    setLoading(true);
    try {
      if (isGuest) {
        const response = await publicSamplesApi.getSampleArticleList();
        const data = response?.articles || [];
        setItems(Array.isArray(data) ? data : []);
        setTotalCount(response?.total_count || data.length);
        if (response?.all_tags) setAllTags(response.all_tags);
        setLoading(false);
        return;
      }
      const skip = (page - 1) * pageSize;
      const params = { 
        skip, 
        limit: pageSize,
        search: searchText || undefined,
        status: statusFilter || undefined,
        tag: tagFilter || undefined,
      };
      if ((userRole === 'admin' || userRole === 'master') && selectedUserId) {
        params.user_id = selectedUserId;
      }
      const response = await v2ArticlesApi.getArticles(params);
      const data = response?.articles || response || [];
      setItems(Array.isArray(data) ? data : []);
      setTotalCount(response?.total_count || data.length);
      if (response?.all_tags) {
        setAllTags(response.all_tags);
      }
    } catch (e) {
      console.error('Failed to get articles:', e);
      setItems([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };
  
  // タグ追加
  const handleAddTag = async (uuid, newTag) => {
    if (!newTag.trim()) return;
    try {
      await v2ArticlesApi.addTag(uuid, newTag.trim());
      message.success('タグを追加しました');
      load();
    } catch (e) {
      console.error('Failed to add tag:', e);
      message.error('タグの追加に失敗しました');
    }
    setTagInputVisible({});
    setTagInputValue('');
  };
  
  // タグ削除
  const handleRemoveTag = async (uuid, tagToRemove, e) => {
    e.stopPropagation();
    try {
      await v2ArticlesApi.removeTag(uuid, tagToRemove);
      message.success('タグを削除しました');
      load();
    } catch (e) {
      console.error('Failed to remove tag:', e);
      message.error('タグの削除に失敗しました');
    }
  };

  useEffect(() => {
    if (userRole || isGuest) {
      load();
    }
  }, [userRole, isGuest, selectedUserId, statusFilter, tagFilter]);
  
  // 検索テキスト変更時に1ページ目に戻して再読み込み
  useEffect(() => {
    if (userRole && !isGuest) {
      setPageState(prev => ({ ...prev, current: 1 }));
      load(1, pageState.pageSize);
    }
  }, [searchText]);

  // 2クリック削除ハンドラ
  const handleDeleteClick = (uuid, e) => {
    e.stopPropagation();
    
    if (deleteConfirmId === uuid) {
      // 2回目のクリック → 削除実行
      if (deleteConfirmTimerRef.current) {
        clearTimeout(deleteConfirmTimerRef.current);
        deleteConfirmTimerRef.current = null;
      }
      setDeleteConfirmId(null);
      
      const doDelete = async () => {
        try {
          await v2ArticlesApi.deleteArticle(uuid);
          message.success('生成結果を削除しました');
          load();
        } catch (error) {
          console.error('Failed to delete article:', error);
          message.error('削除に失敗しました');
        }
      };
      doDelete();
    } else {
      // 1回目のクリック → 確認状態に
      setDeleteConfirmId(uuid);
      
      // 3秒後に確認状態をリセット
      if (deleteConfirmTimerRef.current) {
        clearTimeout(deleteConfirmTimerRef.current);
      }
      deleteConfirmTimerRef.current = setTimeout(() => {
        setDeleteConfirmId(null);
      }, 3000);
    }
  };

  const columns = useMemo(() => {
    const cols = [
      {
        title: '',
        key: 'open_new_tab',
        width: 40,
        render: (_, rec) => (
          <Tooltip title="別タブで開く">
            <span
              onClick={e => {
                e.stopPropagation();
                const norm = normalizeStatus(rec);
                const currentStep = String(rec.current_step || '').toLowerCase();
                let path = `/aio/generation/${rec.uuid}`;
                if (norm === 'completed' || currentStep === '4.4' || currentStep === 'step4.4') {
                  path = `/aio/generation/${rec.uuid}/step4-4`;
                } else if (currentStep === '4.3' || currentStep === 'step4.3') {
                  path = `/aio/generation/${rec.uuid}/step4-3`;
                } else if (currentStep === '4.2' || currentStep === 'step4.2') {
                  path = `/aio/generation/${rec.uuid}/step4-2`;
                } else if (currentStep === '4.1' || currentStep === 'step4.1') {
                  path = `/aio/generation/${rec.uuid}/step4-1`;
                } else if (norm === 'step3') {
                  path = `/aio/generation/${rec.uuid}/step3`;
                } else if (norm === 'step2') {
                  path = `/aio/generation/${rec.uuid}/step2`;
                }
                window.open(path, '_blank');
              }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 24,
                height: 24,
                border: '1px solid #d9d9d9',
                borderRadius: 4,
                cursor: 'pointer',
                transition: 'all 0.2s',
                background: '#fff',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.borderColor = '#1890ff';
                e.currentTarget.style.color = '#1890ff';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.borderColor = '#d9d9d9';
                e.currentTarget.style.color = 'inherit';
              }}
            >
              <ExportOutlined style={{ fontSize: 12 }} />
            </span>
          </Tooltip>
        ),
      },
      {
        title: 'キーワード',
        dataIndex: 'keyword',
        key: 'keyword',
        render: text => <strong>{text}</strong>,
        sorter: (a, b) =>
          String(a.keyword || '').localeCompare(String(b.keyword || '')),
      },
      {
        title: 'ステータス',
        key: 'status',
        width: 120,
        render: (_, rec) => {
          const norm = normalizeStatus(rec);
          const meta = statusMap[norm] || {
            text: rec.status,
            color: 'default',
          };
          return <Tag color={meta.color}>{meta.text}</Tag>;
        },
      },
      {
        title: 'タグ',
        key: 'tags',
        width: 200,
        render: (_, rec) => {
          const tags = rec.tags || [];
          if (isGuest || rec.is_sample) {
            return (
              <Space size={4} wrap>
                {tags.map(tag => (
                  <Tag key={tag} style={{ marginRight: 4 }}>{tag}</Tag>
                ))}
              </Space>
            );
          }
          const isInputVisible = tagInputVisible[rec.uuid];
          return (
            <Space size={4} wrap onClick={e => e.stopPropagation()}>
              {tags.map(tag => (
                <Tag
                  key={tag}
                  closable
                  onClose={e => handleRemoveTag(rec.uuid, tag, e)}
                  style={{ marginRight: 4 }}
                >
                  {tag}
                </Tag>
              ))}
              {isInputVisible ? (
                <AutoComplete
                  size="small"
                  style={{ width: 120 }}
                  value={tagInputValue}
                  onChange={v => setTagInputValue(v)}
                  onBlur={() => {
                    handleAddTag(rec.uuid, tagInputValue);
                  }}
                  onSelect={(v) => {
                    handleAddTag(rec.uuid, v);
                  }}
                  options={allTags
                    .filter(t => 
                      !tags.includes(t) && 
                      t.toLowerCase().includes((tagInputValue || '').toLowerCase())
                    )
                    .map(t => ({ value: t, label: t }))}
                  placeholder="タグを入力"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddTag(rec.uuid, tagInputValue);
                    }
                  }}
                />
              ) : (
                <Tag
                  style={{ 
                    background: '#fff', 
                    borderStyle: 'dashed',
                    cursor: 'pointer',
                  }}
                  onClick={e => {
                    e.stopPropagation();
                    setTagInputVisible({ [rec.uuid]: true });
                    setTagInputValue('');
                  }}
                >
                  <PlusOutlined />
                </Tag>
              )}
            </Space>
          );
        },
      },
      {
        title: 'スタイル',
        key: 'style',
        width: 180,
        render: (_, rec) => {
          const styleName = rec.style_name || '-';

          return (
            <Space direction="vertical" size={0}>
              <Typography.Text style={{ fontSize: 13 }}>
                {styleName}
              </Typography.Text>
              {rec.has_style_overrides && (
                <Tag
                  color="orange"
                  icon={<SettingOutlined />}
                  style={{ fontSize: 11, margin: 0 }}
                >
                  微調整
                </Tag>
              )}
            </Space>
          );
        },
      },
      {
        title: '最終更新日',
        dataIndex: 'updated_at',
        key: 'updated',
        render: (v, rec) => {
          // updated_at > completed_at > created_at の優先順位
          const d = rec.updated_at || rec.completed_at || rec.created_at;
          if (!d) return '-';
          try {
            const dt = new Date(d);
            const y = dt.getFullYear();
            const m = String(dt.getMonth() + 1).padStart(2, '0');
            const day = String(dt.getDate()).padStart(2, '0');
            const hh = String(dt.getHours()).padStart(2, '0');
            const mm = String(dt.getMinutes()).padStart(2, '0');
            return `${y}/${m}/${day} ${hh}:${mm}`;
          } catch (_) {
            return d;
          }
        },
        sorter: (a, b) =>
          new Date(a.updated_at || a.completed_at || a.created_at || 0) -
          new Date(b.updated_at || b.completed_at || b.created_at || 0),
      },
    ];
    if (isAdmin) {
      cols.push({
        title: '作成者',
        key: 'owner',
        width: 80,
        align: 'center',
        render: (_, record) => (
          <Tooltip
            title={
              <span style={{ whiteSpace: 'pre-line' }}>
                {[record.company_name, record.user_name, record.user_email]
                  .filter(Boolean)
                  .join('\n')}
              </span>
            }
          >
            <span
              onClick={e => e.stopPropagation()}
              style={{
                display: 'inline-flex',
                width: 28,
                height: 28,
                borderRadius: '50%',
                background: '#f0f0f0',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 600,
                color: '#595959',
              }}
            >
              {(record.user_name || record.user_email || 'U')
                .toString()
                .trim()
                .slice(0, 1)
                .toUpperCase()}
            </span>
          </Tooltip>
        ),
      });
    }
    // 削除カラム（2クリックで削除）
      cols.push({
        title: '',
      key: 'delete',
      width: 48,
        align: 'center',
      render: (_, rec) => {
        if (rec.is_sample) return null;
        const isConfirming = deleteConfirmId === rec.uuid;
        return (
          <Tooltip title={isConfirming ? 'もう一度クリックで削除' : '削除'}>
            <DeleteOutlined
              onClick={e => handleDeleteClick(rec.uuid, e)}
              style={{ 
                color: isConfirming ? '#ff4d4f' : '#999',
                cursor: 'pointer',
                fontSize: isConfirming ? 18 : 16,
                transition: 'all 0.2s',
              }}
                />
              </Tooltip>
        );
      },
      });
    return cols;
  }, [isAdmin, navigate, deleteConfirmId, tagInputVisible, tagInputValue]);

  return (
    <>
      <Breadcrumb style={{ margin: '16px 0' }}>
        <Breadcrumb.Item>
          <Link to="/aio/dashboard">AIOコンテンツモード</Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item>生成結果一覧</Breadcrumb.Item>
      </Breadcrumb>

      <div style={{ marginBottom: 24 }}>
        <Typography.Title
          level={1}
          className="gradient-text"
          style={{ fontSize: 24 }}
        >
          生成結果一覧
        </Typography.Title>
        <Typography.Text type="secondary">
          最近の生成結果を確認・管理できます
        </Typography.Text>
      </div>

      <div
        className="result-list-actions"
        style={{
          marginBottom: 16,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <Space wrap>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              if (isGuest) {
                setRegisterAction('新規生成');
                setShowRegisterModal(true);
              } else {
                navigate('/aio/generation/new');
              }
            }}
            style={{ minWidth: 160, fontWeight: 'bold' }}
          >
            新規生成
          </Button>
          <Input
            placeholder="キーワードで検索..."
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            allowClear
            style={{ minWidth: 200 }}
          />
          <Select
            allowClear
            placeholder="ステータス"
            value={statusFilter}
            onChange={v => {
              setStatusFilter(v);
              setPageState(prev => ({ ...prev, current: 1 }));
            }}
            style={{ minWidth: 140 }}
            options={[
              { value: null, label: '全てのステータス' },
              { value: 'queued', label: '準備中' },
              { value: 'step2', label: 'ベース選択中' },
              { value: 'step3', label: '構成編集中' },
              { value: 'step4_1', label: '執筆計画中' },
              { value: 'step4_2', label: '本文生成中' },
              { value: 'step4_3', label: '推敲中' },
              { value: 'step4_4', label: '最終調整中' },
              { value: 'completed', label: '生成完了' },
              { value: 'error', label: 'エラー' },
            ]}
          />
          <Select
            allowClear
            placeholder="タグ"
            value={tagFilter}
            onChange={v => {
              setTagFilter(v);
              setPageState(prev => ({ ...prev, current: 1 }));
            }}
            style={{ minWidth: 140 }}
            options={[
              { value: null, label: '全てのタグ' },
              ...allTags.map(tag => ({ value: tag, label: tag })),
            ]}
          />
          {(userRole === 'admin' || userRole === 'master') && (
            <Select
              allowClear
              showSearch
              placeholder="全てのユーザー"
              value={selectedUserId}
              onChange={setSelectedUserId}
              style={{ minWidth: 200 }}
              filterOption={(input, option) =>
                (option?.label ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              options={[
                { value: null, label: '全てのユーザー' },
                ...users.map(user => ({
                  value: user.user_id,
                  label: `${user.last_name}${user.first_name} (${user.email})${
                    user.company_name ? ` - ${user.company_name}` : ''
                  }`,
                })),
              ]}
            />
          )}
        </Space>
        <span />
      </div>

      <Table
        rowKey={r => r.uuid}
        loading={loading}
        columns={columns}
        dataSource={items}
        scroll={{ x: 'max-content' }}
        onRow={rec => ({
          onClick: () => {
            if (rec.is_sample) {
              navigate(`/aio/generation/${rec.uuid}`);
              return;
            }
            const norm = normalizeStatus(rec);
            const currentStep = String(rec.current_step || '').toLowerCase();

            if (norm === 'completed') {
              navigate(`/aio/generation/${rec.uuid}/step4-4`);
              return;
            }

            // STEP4のサブステップに応じた遷移
            if (
              norm === 'step4' ||
              norm === 'step4_1' ||
              norm === 'step4_2' ||
              norm === 'step4_3' ||
              norm === 'step4_4' ||
              norm === 'step4_ready' ||
              norm === 'writing' ||
              norm === 'reviewing' ||
              norm === 'finalizing'
            ) {
              // current_stepが4.4や4.3などの場合は適切なサブステップに遷移
              if (currentStep === '4.4' || currentStep === 'step4.4') {
                navigate(`/aio/generation/${rec.uuid}/step4-4`);
              } else if (currentStep === '4.3' || currentStep === 'step4.3') {
                navigate(`/aio/generation/${rec.uuid}/step4-3`);
              } else if (currentStep === '4.2' || currentStep === 'step4.2') {
                navigate(`/aio/generation/${rec.uuid}/step4-2`);
              } else if (currentStep === '4.1' || currentStep === 'step4.1') {
                navigate(`/aio/generation/${rec.uuid}/step4-1`);
              } else {
                // デフォルトは4.1（執筆計画）
                navigate(`/aio/generation/${rec.uuid}/step4-1`);
              }
            } else if (norm === 'step3') {
              navigate(`/aio/generation/${rec.uuid}/step3`);
            } else if (norm === 'step2') {
              // STEP2: ベース選択中
              navigate(`/aio/generation/${rec.uuid}/step2`);
            } else if (norm === 'step1_generating') {
              // STEP1完了後、構成案生成中: STEP2（WebSocket再接続あり）に遷移
              navigate(`/aio/generation/${rec.uuid}/step2`);
            } else if (norm === 'error' || norm === 'cancelled') {
              // エラー/キャンセル: 詳細画面（STEP1）に遷移
              navigate(`/aio/generation/${rec.uuid}`);
            } else {
              // その他（queued等）: 詳細画面に遷移
              navigate(`/aio/generation/${rec.uuid}`);
            }
          },
          style: { cursor: 'pointer' },
        })}
        locale={{ emptyText: <Empty description="生成結果がありません" /> }}
        pagination={{
          current: pageState.current,
          pageSize: pageState.pageSize,
          total: totalCount,
          showSizeChanger: true,
          showTotal: total => `全 ${total} 件`,
          onChange: (page, pageSize) => {
            setPageState({ current: page, pageSize });
            load(page, pageSize);
          },
        }}
      />

      <RegisterPromptModal
        open={showRegisterModal}
        onClose={() => setShowRegisterModal(false)}
        triggerAction={registerAction}
      />
    </>
  );
};

export default ResultList;
